import React, { useState, useEffect } from 'react';
import './Skills.css';

const Skills = () => {
  const [animated, setAnimated] = useState(false);

  const frontendSkills = [
    { name: 'HTML/CSS', percentage: 95, icon: 'fab fa-html5', color: '#e34f26' },
    { name: 'JavaScript', percentage: 90, icon: 'fab fa-js-square', color: '#f7df1e' },
    { name: 'TypeScript', percentage: 85, icon: 'fab fa-js-square', color: '#3178c6' },
    { name: 'React.js', percentage: 92, icon: 'fab fa-react', color: '#61dafb' },
    { name: 'Next.js', percentage: 88, icon: 'fas fa-bolt', color: '#000000' },
    { name: 'Vue.js', percentage: 75, icon: 'fab fa-vuejs', color: '#4fc08d' }
  ];

  const mobileSkills = [
    { name: 'React Native', percentage: 88, icon: 'fab fa-react', color: '#61dafb' },
    { name: 'Expo', percentage: 85, icon: 'fas fa-mobile-alt', color: '#000020' },
    { name: 'iOS Development', percentage: 70, icon: 'fab fa-apple', color: '#000000' },
    { name: 'Android Development', percentage: 72, icon: 'fab fa-android', color: '#3ddc84' },
    { name: 'Flutter', percentage: 65, icon: 'fas fa-mobile', color: '#02569b' },
    { name: 'Mobile UI/UX', percentage: 82, icon: 'fas fa-paint-brush', color: '#ff6b6b' }
  ];

  const automationSkills = [
    { name: 'n8n Workflow Automation', percentage: 90, icon: 'fas fa-robot', color: '#ff6b35' },
    { name: 'AI Integration', percentage: 88, icon: 'fas fa-brain', color: '#00b4d8' },
    { name: 'API Orchestration', percentage: 92, icon: 'fas fa-network-wired', color: '#9d4edd' },
    { name: 'Webhook Development', percentage: 85, icon: 'fas fa-plug', color: '#f72585' },
    { name: 'Data Processing', percentage: 87, icon: 'fas fa-database', color: '#4cc9f0' },
    { name: 'Chatbot Development', percentage: 83, icon: 'fas fa-comments', color: '#4361ee' }
  ];

  const POSskills = [
    { name: 'POS System Design', percentage: 80, icon: 'fas fa-cash-register', color: '#ff9f1c' },
    { name: 'Inventory Management', percentage: 78, icon: 'fas fa-boxes', color: '#2ec4b6' },
    { name: 'Payment Gateway Integration', percentage: 85, icon: 'fas fa-credit-card', color: '#e71d36' },
    { name: 'Sales Reporting', percentage: 82, icon: 'fas fa-chart-line', color: '#3a86ff' },
    { name: 'Customer Management', percentage: 75, icon: 'fas fa-users', color: '#8338ec' },
    { name: 'Multi-Platform Support', percentage: 80, icon: 'fas fa-desktop', color: '#ff006e' }
  ];

  const toolsSkills = [
    { name: 'Git/GitHub', percentage: 90, icon: 'fab fa-git-alt', color: '#f05032' },
    { name: 'Webpack', percentage: 80, icon: 'fas fa-cube', color: '#8dd6f9' },
    { name: 'Redux', percentage: 85, icon: 'fas fa-code-branch', color: '#764abc' },
    { name: 'Jest/Testing', percentage: 78, icon: 'fas fa-vial', color: '#c21325' },
    { name: 'Firebase', percentage: 82, icon: 'fas fa-fire', color: '#ffca28' },
    { name: 'REST APIs', percentage: 88, icon: 'fas fa-cloud', color: '#00b0ff' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const SkillBar = ({ skill, delay }) => (
    <div className="skill-item">
      <div className="skill-header">
        <div className="skill-info">
          <i className={skill.icon} style={{ color: skill.color }}></i>
          <span className="skill-name">{skill.name}</span>
        </div>
        <span className="skill-percentage">{skill.percentage}%</span>
      </div>
      <div className="skill-bar">
        <div 
          className={`skill-progress ${animated ? 'animated' : ''}`}
          style={{ 
            width: animated ? `${skill.percentage}%` : '0%',
            backgroundColor: skill.color,
            transition: `width 1.5s ease ${delay}ms`
          }}
        ></div>
      </div>
    </div>
  );

  const SkillCard = ({ title, skills, category, description }) => (
    <div className={`skill-category ${category}`}>
      <h4 className="category-title">
        <i className={getCategoryIcon(category)}></i>
        {title}
      </h4>
      {description && <p className="category-description">{description}</p>}
      <div className="skills-list">
        {skills.map((skill, index) => (
          <SkillBar 
            key={skill.name} 
            skill={skill} 
            delay={index * 100}
          />
        ))}
      </div>
    </div>
  );

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'frontend': return 'fas fa-desktop';
      case 'mobile': return 'fas fa-mobile-alt';
      case 'automation': return 'fas fa-robot';
      case 'tools': return 'fas fa-tools';
      default: return 'fas fa-code';
    }
  };

  return (
    <section className="section skills" id="skills">
      <div className="container">
        <div className="section-title">
          <h2>Technical Skills</h2>
          <p>Full-stack development with expertise in modern web, mobile, and AI automation technologies</p>
        </div>
        
        <div className="skills-content">
          <div className="skills-grid">
            <SkillCard 
              title="Frontend Development"
              skills={frontendSkills}
              category="frontend"
              description="Building responsive and interactive user interfaces with modern frameworks"
            />
            
            <SkillCard 
              title="Mobile Development"
              skills={mobileSkills}
              category="mobile"
              description="Cross-platform mobile applications for iOS and Android"
            />
            
            <SkillCard 
              title="AI & Workflow Automation"
              skills={automationSkills}
              category="automation"
              description="Intelligent automation solutions using n8n and AI integrations"
            />

            <SkillCard 
              title="POS Systems Development"
              skills={POSskills}
              category="POS"
              description="Custom POS systems for retail and hospitality businesses"
            />

            <SkillCard 
              title="Tools & Technologies"
              skills={toolsSkills}
              category="tools"
              description="Development tools and technologies for efficient workflow"
            />
          </div>
          
          <div className="skills-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fab fa-react"></i>
              </div>
              <h3>React Specialist</h3>
              <p>
                Extensive experience in building scalable React applications 
                with modern hooks, context API, and state management solutions.
              </p>
              <div className="expertise-tags">
                <span className="tag">React Hooks</span>
                <span className="tag">Context API</span>
                <span className="tag">Redux Toolkit</span>
                <span className="tag">React Router</span>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <h3>AI Automation Expert</h3>
              <p>
                Specialized in workflow automation using n8n, AI integrations, 
                and creating intelligent business process automation solutions.
              </p>
              <div className="expertise-tags">
                <span className="tag">n8n Workflows</span>
                <span className="tag">AI Integration</span>
                <span className="tag">API Orchestration</span>
                <span className="tag">Webhook Development</span>
                <span className="tag">Data Processing</span>
                <span className="tag">Chatbot Systems</span>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3>React Native Expert</h3>
              <p>
                Proficient in cross-platform mobile development using React Native, 
                Expo, and native modules for both iOS and Android.
              </p>
              <div className="expertise-tags">
                <span className="tag">Expo SDK</span>
                <span className="tag">Native Modules</span>
                <span className="tag">Mobile UI</span>
                <span className="tag">App Store</span>
                <span className="tag">Play Store</span>
              </div>
            </div>
          </div>
        </div>

        <div className="automation-showcase">
          <div className="automation-content">
            <div className="automation-text">
              <h3>Workflow Automation with n8n</h3>
              <p>
                I specialize in creating sophisticated workflow automations using n8n that 
                integrate AI, APIs, and various services to streamline business processes 
                and enhance productivity.
              </p>
              <div className="automation-features">
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Custom workflow design and implementation</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>AI-powered automation solutions</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Multi-service API integrations</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Real-time data processing</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Custom node development</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Workflow optimization and monitoring</span>
                </div>
              </div>
            </div>
            <div className="automation-visual">
              <div className="workflow-diagram">
                <div className="node trigger-node">
                  <i className="fas fa-play"></i>
                  <span>Trigger</span>
                </div>
                <div className="connector"></div>
                <div className="node process-node">
                  <i className="fas fa-cog"></i>
                  <span>Process</span>
                </div>
                <div className="connector"></div>
                <div className="node ai-node">
                  <i className="fas fa-brain"></i>
                  <span>AI</span>
                </div>
                <div className="connector"></div>
                <div className="node output-node">
                  <i className="fas fa-check"></i>
                  <span>Output</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="technologies-showcase">
          <h3>Technologies I Work With</h3>
          <div className="tech-grid">
            <div className="tech-item">
              <i className="fab fa-react"></i>
              <span>React</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-mobile-alt"></i>
              <span>React Native</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-robot"></i>
              <span>n8n</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-js"></i>
              <span>JavaScript</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-typescript"></i>
              <span>TypeScript</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-node-js"></i>
              <span>Node.js</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>MongoDB</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-brain"></i>
              <span>AI APIs</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-git"></i>
              <span>Git</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-cloud"></i>
              <span>REST APIs</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-plug"></i>
              <span>Webhooks</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-network-wired"></i>
              <span>API Integration</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-network-wired"></i>
              <span>PHP Developer</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;